import { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Mail, Plus, Trash2, Clock,
  Search, MoreVertical, Pencil, ExternalLink, Sparkles,
  LayoutTemplate, MousePointerClick, Zap, Send, CheckCircle2, SortAsc,
  RefreshCw, AlertCircle, Loader2,
} from 'lucide-react';
import PageHeader, { PrimaryButton, PageKPIItem } from 'components/ui/PageHeader';
import { PageContainer } from 'components/ui/page-container';
import { EmbeddedEditor } from './EmbeddedEditor';
import { useChatAssistantStore } from '../chat-assistant/store';
import { designFromHtml } from './services/htmlDesign';
import { generateId } from './utils';
import type { BuilderMode, Design } from './types/editor.types';
import {
  useListTemplates,
  useSaveTemplate,
  useUpdateTemplate,
  useRenameTemplate,
  useMoveTemplateToTrash,
  useTemplate,
} from './hooks';
import type { ITemplateMeta } from './api';
import { SelectField } from "components/ui/select-field";

/* ─── Constants ─────────────────────────────────────────────────────────── */
const EMAIL_GRADIENT = 'from-violet-500 to-indigo-600';
const EMAIL_ACCENT = 'bg-gradient-to-r from-violet-500 to-indigo-500';
const EMAIL_ICON_BG = 'bg-violet-50 dark:bg-violet-900/20';
const EMAIL_ICON_CLR = 'text-violet-600 dark:text-violet-400';

const SORT_OPTIONS = [
  { value: 'modifiedDate', label: 'Last Modified' },
  { value: 'createdDate', label: 'Date Created' },
  { value: 'name', label: 'Name A–Z' },
] as const;

type SortKey = typeof SORT_OPTIONS[number]['value'];

/* ─── Editor State ───────────────────────────────────────────────────────── */
type EditorState =
  | { kind: 'new'; name: string }
  | { kind: 'existing'; id: number; name: string }
  /* Opened from an assistant-generated template — no server id yet, and the
     document comes from HTML rather than a stored design. */
  | { kind: 'generated'; name: string; html: string };

/* ─── Feature Highlights (empty state) ──────────────────────────────────── */
const EMAIL_FEATURES = [
  {
    icon: MousePointerClick,
    title: 'Drag-and-Drop Editor',
    description: 'Build pixel-perfect email layouts without writing a single line of code.',
    gradient: EMAIL_GRADIENT,
    delay: 0,
  },
  {
    icon: Zap,
    title: 'Responsive by Default',
    description: 'Every email looks flawless on desktop, tablet, and mobile — automatically.',
    gradient: 'from-blue-500 to-cyan-500',
    delay: 60,
  },
  {
    icon: Send,
    title: 'Ready to Send',
    description: 'Export HTML or send directly through campaigns in one click.',
    gradient: 'from-indigo-500 to-violet-600',
    delay: 120,
  },
] as const;

/* ─── Helpers ────────────────────────────────────────────────────────────── */
function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return '—';
  }
}

function isThisMonth(iso: string | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  const n = new Date();
  return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
}

function isThisWeek(iso: string | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay());
  weekStart.setHours(0, 0, 0, 0);
  return d >= weekStart;
}

function defaultEmptyDesign(): Design {
  return {
    id: generateId(),
    version: '1.0',
    mode: 'email' as BuilderMode,
    body: { rows: [], styles: { backgroundColor: '#ffffff', padding: '0px' } },
    metadata: {},
  };
}

/* ─── Template Card ──────────────────────────────────────────────────────── */
function TemplateCard({
  template,
  onOpen,
  onDelete,
  onRename,
  isRenaming,
}: {
  template: ITemplateMeta;
  onOpen: () => void;
  onDelete: () => void;
  onRename: (name: string) => void;
  isRenaming: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(template.name);

  const commitRename = () => {
    const trimmed = newName.trim();
    if (trimmed && trimmed !== template.name) onRename(trimmed);
    setRenaming(false);
  };

  return (
    <div
      onClick={() => { if (!menuOpen && !renaming) onOpen(); }}
      className="group relative bg-surface dark:bg-dark-800
        border border-line dark:border-dark-700
        rounded-2xl overflow-hidden cursor-pointer
        shadow-card hover:shadow-card-hover
        hover:-translate-y-0.5 hover:border-violet-300/60 dark:hover:border-violet-700/60
        transition-all duration-200 animate-card-enter"
    >
      {/* Accent bar */}
      <div className={`absolute top-0 inset-x-0 h-[2px] ${EMAIL_ACCENT}`} />

      {/* Thumbnail */}
      <div className="h-36 bg-gray-50 dark:bg-dark-900 flex items-center justify-center relative mt-[2px]">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-50/60 to-indigo-50/40
          dark:from-violet-900/10 dark:to-indigo-900/10" />

        <div className={`relative w-14 h-14 rounded-2xl flex items-center justify-center
          bg-gradient-to-br ${EMAIL_GRADIENT} shadow-lg
          group-hover:scale-105 transition-transform duration-200`}>
          <Mail className="w-7 h-7 text-white" />
        </div>

        {/* Hover overlay pill */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200
            text-[11px] font-semibold text-violet-600 dark:text-violet-400
            bg-surface dark:bg-dark-800 border border-violet-200 dark:border-violet-700/50
            px-3 py-1 rounded-full shadow-sm flex items-center gap-1.5">
            <ExternalLink className="w-3 h-3" /> Open Editor
          </span>
        </div>

        {/* ⋮ menu */}
        <div className="absolute top-2.5 right-2.5 z-10">
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
            className="w-7 h-7 rounded-lg bg-surface dark:bg-dark-700
              border border-line dark:border-dark-600
              flex items-center justify-center cursor-pointer
              opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity duration-150
              hover:bg-gray-50 dark:hover:bg-dark-600 shadow-sm"
          >
            <MoreVertical className="w-3.5 h-3.5 text-content-muted dark:text-dark-400" />
          </button>

          {menuOpen && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="absolute right-0 top-9 w-40 bg-surface dark:bg-dark-800
                rounded-xl border border-line-subtle dark:border-dark-600
                shadow-elevated z-20 p-1 animate-slide-in-up"
            >
              {[
                { label: 'Open', icon: ExternalLink, action: () => { setMenuOpen(false); onOpen(); } },
                { label: 'Rename', icon: Pencil, action: () => { setRenaming(true); setMenuOpen(false); } },
                { label: 'Delete', icon: Trash2, action: () => { setMenuOpen(false); onDelete(); }, danger: true },
              ].map(({ label, icon: IIcon, action, danger }) => (
                <button
                  key={label}
                  onClick={action}
                  disabled={label === 'Rename' && isRenaming}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg
                    text-xs font-medium transition-colors cursor-pointer border-none bg-transparent text-left
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${danger
                      ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                      : 'text-gray-700 dark:text-dark-300 hover:bg-gray-50 dark:hover:bg-dark-700'
                    }`}
                >
                  {label === 'Rename' && isRenaming
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <IIcon className="w-3.5 h-3.5" />
                  }
                  {label === 'Rename' && isRenaming ? 'Renaming…' : label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        {renaming ? (
          <input
            autoFocus
            value={newName}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setNewName(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenaming(false); }}
            className="w-full text-sm font-semibold border-0 border-b-2 border-violet-500
              outline-none bg-transparent text-content dark:text-white"
          />
        ) : (
          <p title={template.name} className="text-sm font-semibold text-content dark:text-white truncate leading-snug">
            {template.name}
          </p>
        )}
        <div className="flex items-center justify-between mt-2">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${EMAIL_ICON_BG} ${EMAIL_ICON_CLR}`}>
            Email
          </span>
          <span className="text-[10px] text-content-subtle dark:text-dark-500 flex items-center gap-1">
            <Clock className="w-2.5 h-2.5" />
            {formatDate(template.modifiedDate ?? template.updated_at ?? template.createdDate ?? template.created_at)}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── New Template Modal ─────────────────────────────────────────────────── */
function NewTemplateModal({ open, onClose, onCreate }: {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string) => void;
}) {
  const [name, setName] = useState('');

  if (!open) return null;

  const handleCreate = () => {
    if (!name.trim()) return;
    onCreate(name.trim());
    setName('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-surface dark:bg-dark-800 rounded-2xl w-full max-w-sm
        shadow-elevated border border-line-subtle dark:border-dark-600 overflow-hidden animate-slide-in-up">

        {/* Header */}
        <div className={`bg-gradient-to-r ${EMAIL_GRADIENT} px-6 py-5`}>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
              <Mail className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white leading-tight">Design New Template</h2>
              <p className="text-xs text-white/65 mt-0.5">Name your template to get started</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <label className="block text-[11px] font-semibold text-content-muted dark:text-dark-400 uppercase tracking-wider mb-1.5">
            Template Name
          </label>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            placeholder="e.g. Welcome Series, Black Friday…"
            className="w-full px-3.5 py-2.5 rounded-xl border border-line dark:border-dark-600
              bg-gray-50 dark:bg-dark-900 text-sm text-content dark:text-white
              placeholder-gray-400 dark:placeholder-dark-500 mb-6
              focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400 transition-all"
          />

          <div className="flex gap-2.5 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-line dark:border-dark-600
                bg-surface dark:bg-dark-900 text-sm font-semibold text-content-secondary dark:text-dark-400
                hover:bg-gray-50 dark:hover:bg-dark-800 cursor-pointer transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={!name.trim()}
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-xl border-0
                text-sm font-bold cursor-pointer transition-all
                bg-gradient-to-r ${EMAIL_GRADIENT}
                hover:opacity-90 hover:shadow-md
                text-white shadow-sm
                disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <CheckCircle2 className="w-4 h-4" />
              Create Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Template Loading Screen ────────────────────────────────────────────── */
function TemplateLoadingScreen({ name, onBack }: { name: string; onBack: () => void }) {
  return (
    <div className="flex flex-col h-full">
      {/* Mini toolbar */}
      <div className="flex items-center gap-3 px-4 h-12 border-b border-line dark:border-dark-700
        bg-surface dark:bg-dark-900">
        <button onClick={onBack} className="text-xs text-content-muted hover:text-gray-700 dark:text-dark-400 dark:hover:text-white transition-colors">
          ← Back
        </button>
        <span className="text-sm font-semibold text-gray-700 dark:text-white">{name}</span>
      </div>
      {/* Loading body */}
      <div className="flex-1 flex flex-col items-center justify-center gap-4 bg-[#f0f2f8] dark:bg-dark-950">
        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${EMAIL_GRADIENT}
          flex items-center justify-center shadow-lg animate-pulse`}>
          <Mail className="w-8 h-8 text-white" />
        </div>
        <div className="flex items-center gap-2 text-sm text-content-muted dark:text-dark-400">
          <Loader2 className="w-4 h-4 animate-spin text-violet-500 dark:text-violet-400" />
          Loading template…
        </div>
      </div>
    </div>
  );
}

/* ─── Skeleton Card ──────────────────────────────────────────────────────── */
function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-line dark:border-dark-700 bg-surface dark:bg-dark-800 overflow-hidden animate-pulse">
      <div className="h-36 bg-gray-100 dark:bg-dark-700" />
      <div className="p-4 space-y-2">
        <div className="h-3.5 w-2/3 rounded bg-gray-200 dark:bg-dark-600" />
        <div className="h-2.5 w-1/3 rounded bg-gray-100 dark:bg-dark-700" />
      </div>
    </div>
  );
}

/* ─── Page ───────────────────────────────────────────────────────────────── */
export default function ContentBuilderPage() {
  /* API hooks */
  const { data: templates = [], isLoading: listLoading, error: listError, refetch } = useListTemplates();
  const { mutateAsync: saveTemplate } = useSaveTemplate();
  const { mutateAsync: updateTemplate } = useUpdateTemplate();
  const { mutateAsync: renameTemplate, isPending: renamePending, variables: renameVars } = useRenameTemplate();
  const { mutateAsync: trashTemplate } = useMoveTemplateToTrash();

  /* Editor state */
  const [editorState, setEditorState] = useState<EditorState | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const savedIdRef = useRef<number | null>(null);  // tracks API id after first save

  /* UI state */
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('modifiedDate');


  /* Fetch design when opening an existing template */
  const existingId = editorState?.kind === 'existing' ? editorState.id : null;
  const { data: loadedTemplate, isLoading: templateLoading } = useTemplate(existingId);

  /* ── Open editor ───────────────────────────────────────────────────────── */
  const openNewEditor = (name: string) => {
    savedIdRef.current = null;
    setEditorKey((k) => k + 1);
    setEditorState({ kind: 'new', name });
  };

  const openExistingEditor = (template: ITemplateMeta) => {
    savedIdRef.current = template.id;
    setEditorKey((k) => k + 1);
    setEditorState({ kind: 'existing', id: template.id, name: template.name });
  };

  const closeEditor = () => setEditorState(null);

  /* ── Pick up a template the assistant generated ─────────────────────────
     The user asks for one from /ai-playground, so no editor is mounted when
     it arrives — the chat store holds it. Arriving here with one pending
     opens the editor on it instead of an empty canvas.

     Consumed once: `clearGeneratedTemplate` runs on open so returning to this
     page later does not re-open a template the user already dismissed.     */
  const generated = useChatAssistantStore((st) => st.generatedTemplate);
  const clearGenerated = useChatAssistantStore((st) => st.clearGeneratedTemplate);
  const openedGeneratedRef = useRef(false);

  useEffect(() => {
    if (!generated || openedGeneratedRef.current || editorState) return;
    openedGeneratedRef.current = true;
    savedIdRef.current = null;
    setEditorKey((k) => k + 1);
    setEditorState({ kind: 'generated', name: 'Untitled template', html: generated.html });
    clearGenerated();
  }, [generated, editorState, clearGenerated]);

  /* ── Save callback (passed to EmbeddedEditor) ──────────────────────────── */
  const handleEditorSave = async (design: Design) => {
    if (!editorState) return;
    const name = editorState.name;

    if (savedIdRef.current == null) {
      // First save of a new template → POST
      const result = await saveTemplate({ name, design });
      savedIdRef.current = result?.id ?? null;
    } else {
      // Subsequent saves → PUT
      await updateTemplate({ id: savedIdRef.current, name, design });
    }
  };

  /* ── Filtered + sorted list ────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const list = templates.filter((t) =>
      t.name.toLowerCase().includes(search.toLowerCase()),
    );
    return [...list].sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name);
      // Accept both spec field names and fallback aliases
      const getDate = (t: ITemplateMeta) =>
        (t[sortKey] as string | undefined) ??
        (sortKey === 'modifiedDate' ? (t.updated_at as string | undefined) : (t.created_at as string | undefined)) ??
        '';
      return getDate(b).localeCompare(getDate(a));
    });
  }, [templates, search, sortKey]);

  /* ── KPI cards ─────────────────────────────────────────────────────────── */
  const lastModified = templates.length > 0
    ? templates.reduce((latest, t) => {
      const d = t.modifiedDate ?? t.updated_at ?? t.createdDate ?? t.created_at ?? '';
      return d > latest ? d : latest;
    }, '')
    : '';

  const kpis: PageKPIItem[] = [
    {
      icon: LayoutTemplate,
      value: templates.length,
      label: 'Total Templates',
      accentColor: 'bg-gradient-to-r from-violet-500 to-indigo-500',
      accentHex: '#6366f1',
      iconBg: EMAIL_ICON_BG,
      iconColor: EMAIL_ICON_CLR,
    },
    {
      icon: Mail,
      value: templates.filter((t) => isThisMonth(t.createdDate ?? t.created_at)).length,
      label: 'Created This Month',
      accentColor: 'bg-gradient-to-r from-blue-500 to-indigo-500',
      accentHex: '#3b82f6',
      iconBg: 'bg-blue-50 dark:bg-blue-900/20',
      iconColor: 'text-blue-600 dark:text-blue-400',
    },
    {
      icon: Zap,
      value: templates.filter((t) => isThisWeek(t.modifiedDate ?? t.updated_at)).length,
      label: 'Active This Week',
      accentColor: 'bg-gradient-to-r from-indigo-400 to-violet-500',
      accentHex: '#818cf8',
      iconBg: 'bg-indigo-50 dark:bg-indigo-900/20',
      iconColor: 'text-indigo-600 dark:text-indigo-400',
    },
    {
      icon: Send,
      value: formatDate(lastModified),
      label: 'Last Modified',
      accentColor: 'bg-gradient-to-r from-violet-400 to-fuchsia-500',
      accentHex: '#a78bfa',
      iconBg: 'bg-violet-50 dark:bg-violet-900/20',
      iconColor: 'text-violet-600 dark:text-violet-400',
    },
  ];

  /* ── Editor portal ──────────────────────────────────────────────────────── */
  const showEditor = editorState !== null;
  const editorReady =
    editorState?.kind === 'new' ||
    // Nothing to fetch: the HTML arrived with the frame.
    editorState?.kind === 'generated' ||
    (editorState?.kind === 'existing' && !templateLoading && loadedTemplate != null);

  const resolvedDesign: Design | undefined =
    editorState?.kind === 'new'
      ? defaultEmptyDesign()
      : editorState?.kind === 'generated'
        ? designFromHtml(editorState.html)
        : loadedTemplate?.design;

  const editorOverlay = showEditor
    ? createPortal(
      <div className="fixed inset-0 z-[200] flex flex-col bg-gray-50 dark:bg-dark-900">
        {!editorReady ? (
          <TemplateLoadingScreen name={editorState?.name ?? ''} onBack={closeEditor} />
        ) : (
          <EmbeddedEditor
            key={editorKey}
            templateName={editorState?.name ?? ''}
            mode="email"
            initialDesign={resolvedDesign}
            onSave={handleEditorSave}
            onBack={closeEditor}
            templateId={savedIdRef.current}
          />
        )}
      </div>,
      document.body,
    )
    : null;

  /* ─── Render ─────────────────────────────────────────────────────────── */
  return (
    <>
      {editorOverlay}
      <PageContainer>

        {/* ── Page Header ── */}
        <PageHeader
          icon={<Mail className="w-5 h-5" />}
          iconBg={EMAIL_ICON_BG}
          iconColor={EMAIL_ICON_CLR}
          accentColor={EMAIL_ACCENT}
          title="Email Builder"
          subtitle="Design reusable email templates for your campaigns"
          badge={templates.length > 0 ? `${templates.length} template${templates.length !== 1 ? 's' : ''}` : undefined}
          kpis={listLoading ? [] : kpis}
          actions={
            <PrimaryButton
              icon={<Plus className="w-4 h-4" />}
              onClick={() => setNewProjectOpen(true)}
            >
              Design New Template
            </PrimaryButton>
          }
        />

        {/* ── Error State ── */}
        {listError && !listLoading && (
          <div className="mt-6 flex flex-col items-center gap-3 py-12 text-center">
            <div className="w-12 h-12 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-500 dark:text-red-400" />
            </div>
            <p className="text-sm text-content-secondary dark:text-dark-300 font-medium">Failed to load templates</p>
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl
                bg-surface dark:bg-dark-800 border border-line dark:border-dark-600
                text-xs font-semibold text-gray-700 dark:text-dark-300
                hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors cursor-pointer"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Retry
            </button>
          </div>
        )}

        {/* ── Loading Skeletons ── */}
        {listLoading && (
          <div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(210px,1fr))] gap-4">
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        )}

        {/* ── Empty State ── */}
        {!listLoading && !listError && templates.length === 0 && (
          <div className="mt-4 animate-fade-in">

            {/* Hero */}
            <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${EMAIL_GRADIENT} p-6 sm:p-8 mb-5`}>
              <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full bg-white/10 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-8 -left-6 w-36 h-36 rounded-full bg-indigo-400/20 blur-2xl pointer-events-none" />
              <div className="absolute inset-0 opacity-[0.07]"
                style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />

              <div className="relative z-10 flex items-center justify-between gap-6 flex-wrap">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm
                    border border-white/25 flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Mail className="w-7 h-7 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 border border-white/20 mb-2">
                      <Sparkles className="w-3 h-3 text-white/80" />
                      <span className="text-[10px] font-semibold text-white/80 uppercase tracking-wider">
                        Email Template Builder
                      </span>
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white leading-tight">
                      Design your first email template
                    </h2>
                    <p className="text-white/60 text-xs sm:text-sm mt-1">
                      Reusable designs, ready to use in any campaign.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setNewProjectOpen(true)}
                  className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl
                    bg-surface text-violet-700 dark:text-violet-300 font-bold text-sm
                    shadow-lg hover:shadow-xl hover:-translate-y-0.5
                    transition-all duration-200 cursor-pointer border-none"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Create Template</span>
                  <span className="sm:hidden">Create</span>
                </button>
              </div>
            </div>

            {/* Feature highlights */}
            <p className="text-[11px] font-semibold text-content-subtle dark:text-dark-500 uppercase tracking-widest mb-3">
              What you can do
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              {EMAIL_FEATURES.map((feat, i) => {
                const Icon = feat.icon;
                return (
                  <div
                    key={i}
                    style={{ animationDelay: `${feat.delay}ms` }}
                    className="group relative rounded-2xl overflow-hidden
                      bg-surface dark:bg-dark-800
                      border border-line dark:border-dark-700
                      shadow-card p-6 animate-card-enter"
                  >
                    <div className={`absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r ${feat.gradient}`} />
                    <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full bg-gradient-to-br ${feat.gradient} opacity-10 blur-lg`} />
                    <div className={`relative z-10 w-12 h-12 rounded-2xl mb-4 bg-gradient-to-br ${feat.gradient} flex items-center justify-center shadow-sm`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="relative z-10 text-sm font-bold text-content dark:text-white mb-1.5">{feat.title}</h3>
                    <p className="relative z-10 text-meta text-content-muted dark:text-dark-400 leading-relaxed">{feat.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Template Grid ── */}
        {!listLoading && !listError && templates.length > 0 && (
          <div className="mt-2">
            {/* Toolbar: search + sort */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {/* Search */}
              <div className="flex-1 min-w-[180px] relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-subtle" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search templates…"
                  className="w-full h-9 pl-9 pr-3 text-sm rounded-xl
                    border border-line dark:border-dark-600
                    bg-surface dark:bg-dark-800 text-content dark:text-white
                    placeholder-gray-400 dark:placeholder-dark-500
                    focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400 transition-all"
                />
              </div>

              {/* Sort */}
              <div className="flex items-center gap-1.5 h-9">
                <SortAsc className="w-3.5 h-3.5 text-content-subtle pointer-events-none flex-shrink-0" />
                <SelectField
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value as SortKey)}
                  className="h-full pl-2 pr-7 text-xs font-semibold rounded-xl
                    border border-line dark:border-dark-600
                    bg-surface dark:bg-dark-800 text-gray-700 dark:text-gray-200
                    focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400
                    cursor-pointer transition-all appearance-none"
                >
                  {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </SelectField>
              </div>

              {/* Count */}
              <p className="text-[12px] font-semibold text-content-subtle dark:text-dark-500 ml-auto">
                {filtered.length > 0 ? `${filtered.length} template${filtered.length !== 1 ? 's' : ''}` : 'No results'}
              </p>
            </div>

            {filtered.length > 0 ? (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(210px,1fr))] gap-4">
                {filtered.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    onOpen={() => openExistingEditor(template)}
                    onDelete={() => trashTemplate(template.id)}
                    onRename={(newName) => renameTemplate({ id: template.id, newName })}
                    isRenaming={renamePending && renameVars?.id === template.id}
                  />
                ))}

                {/* Quick-create tile */}
                <button
                  onClick={() => setNewProjectOpen(true)}
                  className="group relative rounded-2xl border-2 border-dashed
                    border-line dark:border-dark-600
                    hover:border-violet-400/60 dark:hover:border-violet-500/50
                    bg-transparent hover:bg-violet-50/50 dark:hover:bg-violet-900/10
                    flex flex-col items-center justify-center gap-2 min-h-[192px]
                    cursor-pointer transition-all duration-200"
                >
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${EMAIL_GRADIENT}
                    opacity-0 group-hover:opacity-100 flex items-center justify-center
                    transition-all duration-200 scale-75 group-hover:scale-100 shadow-sm`}>
                    <Plus className="w-5 h-5 text-white" />
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-gray-100 dark:bg-dark-700
                    absolute group-hover:opacity-0 flex items-center justify-center transition-opacity">
                    <Plus className="w-5 h-5 text-content-subtle dark:text-dark-500" />
                  </div>
                  <span className="text-xs font-semibold text-content-subtle dark:text-dark-500
                    group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors mt-1">
                    Design New Template
                  </span>
                </button>
              </div>
            ) : (
              <div className="text-center py-16">
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${EMAIL_GRADIENT}
                  opacity-20 flex items-center justify-center mx-auto mb-3`}>
                  <Search className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm text-content-subtle dark:text-dark-500">
                  No templates match "<span className="font-semibold text-content-secondary dark:text-dark-300">{search}</span>"
                </p>
                <button
                  onClick={() => setSearch('')}
                  className="mt-3 text-xs font-semibold text-violet-600 dark:text-violet-400
                    hover:underline cursor-pointer bg-transparent border-none"
                >
                  Clear search
                </button>
              </div>
            )}
          </div>
        )}

        <NewTemplateModal
          open={newProjectOpen}
          onClose={() => setNewProjectOpen(false)}
          onCreate={openNewEditor}
        />
      </PageContainer>
    </>
  );
}
